import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GridLocaleText } from "@mui/x-data-grid";

/**
 * Labels do MUI DataGrid a partir do namespace i18n `dataGrid`.
 */
export function useDataGridLocaleText() {
  const { t } = useTranslation("dataGrid");

  return useMemo(
    () =>
      /** @type {GridLocaleText} */ ({
        noRowsLabel: t("noRowsLabel"),
        noResultsOverlayLabel: t("noResultsOverlayLabel"),
        toolbarDensity: t("toolbarDensity"),
        toolbarDensityLabel: t("toolbarDensityLabel"),
        toolbarDensityCompact: t("toolbarDensityCompact"),
        toolbarDensityStandard: t("toolbarDensityStandard"),
        toolbarDensityComfortable: t("toolbarDensityComfortable"),
        toolbarColumns: t("toolbarColumns"),
        toolbarColumnsLabel: t("toolbarColumnsLabel"),
        toolbarFilters: t("toolbarFilters"),
        toolbarFiltersLabel: t("toolbarFiltersLabel"),
        toolbarFiltersTooltipHide: t("toolbarFiltersTooltipHide"),
        toolbarFiltersTooltipShow: t("toolbarFiltersTooltipShow"),
        toolbarFiltersTooltipActive: (count) => t("filtersActive", { count }),
        toolbarQuickFilterPlaceholder: t("toolbarQuickFilterPlaceholder"),
        toolbarQuickFilterLabel: t("toolbarQuickFilterLabel"),
        toolbarQuickFilterDeleteIconLabel: t("toolbarQuickFilterDeleteIconLabel"),
        toolbarExport: t("toolbarExport"),
        toolbarExportLabel: t("toolbarExportLabel"),
        toolbarExportCSV: t("toolbarExportCSV"),
        toolbarExportPrint: t("toolbarExportPrint"),
        toolbarExportExcel: t("toolbarExportExcel"),
        columnsManagementSearchTitle: t("columnsManagementSearchTitle"),
        columnsManagementNoColumns: t("columnsManagementNoColumns"),
        columnsManagementShowHideAllText: t("columnsManagementShowHideAllText"),
        columnsManagementReset: t("columnsManagementReset"),
        columnsManagementDeleteIconLabel: t("columnsManagementDeleteIconLabel"),
        filterPanelAddFilter: t("filterPanelAddFilter"),
        filterPanelRemoveAll: t("filterPanelRemoveAll"),
        filterPanelDeleteIconLabel: t("filterPanelDeleteIconLabel"),
        filterPanelLogicOperator: t("filterPanelLogicOperator"),
        filterPanelOperator: t("filterPanelOperator"),
        filterPanelOperatorAnd: t("filterPanelOperatorAnd"),
        filterPanelOperatorOr: t("filterPanelOperatorOr"),
        filterPanelColumns: t("filterPanelColumns"),
        filterPanelInputLabel: t("filterPanelInputLabel"),
        filterPanelInputPlaceholder: t("filterPanelInputPlaceholder"),
        filterOperatorContains: t("filterOperatorContains"),
        filterOperatorDoesNotContain: t("filterOperatorDoesNotContain"),
        filterOperatorEquals: t("filterOperatorEquals"),
        filterOperatorDoesNotEqual: t("filterOperatorDoesNotEqual"),
        filterOperatorStartsWith: t("filterOperatorStartsWith"),
        filterOperatorEndsWith: t("filterOperatorEndsWith"),
        filterOperatorIs: t("filterOperatorIs"),
        filterOperatorNot: t("filterOperatorNot"),
        filterOperatorAfter: t("filterOperatorAfter"),
        filterOperatorOnOrAfter: t("filterOperatorOnOrAfter"),
        filterOperatorBefore: t("filterOperatorBefore"),
        filterOperatorOnOrBefore: t("filterOperatorOnOrBefore"),
        filterOperatorIsEmpty: t("filterOperatorIsEmpty"),
        filterOperatorIsNotEmpty: t("filterOperatorIsNotEmpty"),
        filterOperatorIsAnyOf: t("filterOperatorIsAnyOf"),
        filterValueAny: t("filterValueAny"),
        filterValueTrue: t("filterValueTrue"),
        filterValueFalse: t("filterValueFalse"),
        columnMenuLabel: t("columnMenuLabel"),
        columnMenuShowColumns: t("columnMenuShowColumns"),
        columnMenuManageColumns: t("columnMenuManageColumns"),
        columnMenuFilter: t("columnMenuFilter"),
        columnMenuHideColumn: t("columnMenuHideColumn"),
        columnMenuUnsort: t("columnMenuUnsort"),
        columnMenuSortAsc: t("columnMenuSortAsc"),
        columnMenuSortDesc: t("columnMenuSortDesc"),
        columnHeaderFiltersTooltipActive: (count) => t("filtersActive", { count }),
        columnHeaderFiltersLabel: t("columnHeaderFiltersLabel"),
        columnHeaderSortIconLabel: t("columnHeaderSortIconLabel"),
        footerRowSelected: (count) => t("rowsSelected", { count }),
        footerTotalRows: t("footerTotalRows"),
        footerTotalVisibleRows: (visibleCount, totalCount) =>
          t("footerVisibleRows", { visible: visibleCount, total: totalCount }),
        checkboxSelectionHeaderName: t("checkboxSelectionHeaderName"),
        checkboxSelectionSelectAllRows: t("checkboxSelectionSelectAllRows"),
        checkboxSelectionUnselectAllRows: t("checkboxSelectionUnselectAllRows"),
        checkboxSelectionSelectRow: t("checkboxSelectionSelectRow"),
        checkboxSelectionUnselectRow: t("checkboxSelectionUnselectRow"),
        booleanCellTrueLabel: t("booleanCellTrueLabel"),
        booleanCellFalseLabel: t("booleanCellFalseLabel"),
        actionsCellMore: t("actionsCellMore"),
        pinToLeft: t("pinToLeft"),
        pinToRight: t("pinToRight"),
        unpin: t("unpin"),
        treeDataGroupingHeaderName: t("treeDataGroupingHeaderName"),
        treeDataExpand: t("treeDataExpand"),
        treeDataCollapse: t("treeDataCollapse"),
        groupingColumnHeaderName: t("groupingColumnHeaderName"),
        groupColumn: (name) => t("groupColumn", { name }),
        unGroupColumn: (name) => t("unGroupColumn", { name }),
        detailPanelToggle: t("detailPanelToggle"),
        expandDetailPanel: t("expandDetailPanel"),
        collapseDetailPanel: t("collapseDetailPanel"),
        rowReorderingHeaderName: t("rowReorderingHeaderName"),
        aggregationMenuItemHeader: t("aggregationMenuItemHeader"),
        aggregationFunctionLabelSum: t("aggregationFunctionLabelSum"),
        aggregationFunctionLabelAvg: t("aggregationFunctionLabelAvg"),
        aggregationFunctionLabelMin: t("aggregationFunctionLabelMin"),
        aggregationFunctionLabelMax: t("aggregationFunctionLabelMax"),
        aggregationFunctionLabelSize: t("aggregationFunctionLabelSize"),
        MuiTablePagination: {
          labelRowsPerPage: t("tablePaginationLabelRowsPerPage"),
        },
      }),
    [t]
  );
}
